import { BaseEvents } from '@/shared/utils';

/**
 * Event keys for all catalog-related events.
 * Structured in a hierarchical way to provide clarity and organization.
 */
export const CatalogEventKeys = {
  // Base operations
  catalog: {
    view: 'catalog/view',
    do: 'catalog/do',
    success: 'catalog/success',
    error: 'catalog/error',
  },
  search: {
    view: 'catalog/search/view',
    do: 'catalog/search/do',
    success: 'catalog/search/success',
    error: 'catalog/search/error',
  },
  category: {
    view: 'catalog/category/view',
    do: 'catalog/category/do',
    success: 'catalog/category/success',
    error: 'catalog/category/error',
  },
  filter: {
    view: 'catalog/filter/view',
    do: 'catalog/filter/do',
    success: 'catalog/filter/success',
    error: 'catalog/filter/error',
  },

  // Navigation
  navigate: {
    view: 'catalog/navigate/view',
    do: 'catalog/navigate/do',
    success: 'catalog/navigate/success',
    error: 'catalog/navigate/error',
  },
  navigateBack: {
    do: 'catalog/navigateBack/do',
  },

  // Infinite scroll
  loadMore: {
    do: 'catalog/loadMore/do',
    success: 'catalog/loadMore/success',
    error: 'catalog/loadMore/error',
  },

  // Favorites management
  favourite: {
    create: {
      serie: {
        do: 'catalog/favourite/create/serie/do',
        success: 'catalog/favourite/create/serie/success',
        error: 'catalog/favourite/create/serie/error',
      },
      masterclass: {
        do: 'catalog/favourite/create/masterclass/do',
        success: 'catalog/favourite/create/masterclass/success',
        error: 'catalog/favourite/create/masterclass/error',
      },
      learningPath: {
        do: 'catalog/favourite/create/learningPath/do',
        success: 'catalog/favourite/create/learningPath/success',
        error: 'catalog/favourite/create/learningPath/error',
      },
    },
    delete: {
      serie: {
        do: 'catalog/favourite/delete/serie/do',
        success: 'catalog/favourite/delete/serie/success',
        error: 'catalog/favourite/delete/serie/error',
      },
      masterclass: {
        do: 'catalog/favourite/delete/masterclass/do',
        success: 'catalog/favourite/delete/masterclass/success',
        error: 'catalog/favourite/delete/masterclass/error',
      },
      learningPath: {
        do: 'catalog/favourite/delete/learningPath/do',
        success: 'catalog/favourite/delete/learningPath/success',
        error: 'catalog/favourite/delete/learningPath/error',
      },
    },
  },

  // Redirects
  redirects: {
    to: 'redirects/to',
  },
} as const;

// Type for user data in events
export interface UserData {
  uid: string;
  email: string;
  token?: string;
  refreshToken?: string;
  tokenCreationDate?: string;
}

/**
 * Interface defining all catalog event types and their payloads
 */
export interface CatalogEvents extends BaseEvents {
  // Catalog view events
  'catalog/view': void;
  'catalog/do': void;
  'catalog/success': void;
  'catalog/error': Error;

  // Search events
  'catalog/search/view': void;
  'catalog/search/do': {
    query?: string;
    filters: Record<string, any>;
    page?: any;
  };
  'catalog/search/success': void;
  'catalog/search/error': Error;

  // Category events
  'catalog/category/view': void;
  'catalog/category/do': {
    categoryName?: string;
    filters: Record<string, any>;
    page?: any;
  };
  'catalog/category/success': void;
  'catalog/category/error': Error;

  // Filter events
  'catalog/filter/view': void;
  'catalog/filter/do': { filters: Record<string, any> };
  'catalog/filter/success': void;
  'catalog/filter/error': Error;

  // Navigation events
  'catalog/navigate/view': void;
  'catalog/navigate/do': { slug: string; isExternalPage?: boolean };
  'catalog/navigate/success': { sectionData: any[] };
  'catalog/navigate/error': Error;

  // Navigation back event
  'catalog/navigateBack/do': void;

  // Load more events for infinite scroll
  'catalog/loadMore/do': { currentPage: number };
  'catalog/loadMore/success': { additionalResults: any[] };
  'catalog/loadMore/error': Error;

  // Redirect event
  'redirects/to': string;

  // Create favorite events - Series
  'catalog/favourite/create/serie/do': {
    seriesDocumentId: string;
    userData?: UserData;
  };
  'catalog/favourite/create/serie/success': { documentId: string };
  'catalog/favourite/create/serie/error': Error;

  // Create favorite events - Masterclass
  'catalog/favourite/create/masterclass/do': {
    masterclassDocumentId: string;
    userData?: UserData;
  };
  'catalog/favourite/create/masterclass/success': { documentId: string };
  'catalog/favourite/create/masterclass/error': Error;

  // Create favorite events - Learning Path
  'catalog/favourite/create/learningPath/do': {
    learningPathDocumentId: string;
    userData?: UserData;
  };
  'catalog/favourite/create/learningPath/success': { documentId: string };
  'catalog/favourite/create/learningPath/error': Error;

  // Delete favorite events - Series
  'catalog/favourite/delete/serie/do': {
    favouriteId: string;
    userData?: UserData;
  };
  'catalog/favourite/delete/serie/success': void;
  'catalog/favourite/delete/serie/error': Error;

  // Delete favorite events - Masterclass
  'catalog/favourite/delete/masterclass/do': {
    favouriteId: string;
    userData?: UserData;
  };
  'catalog/favourite/delete/masterclass/success': void;
  'catalog/favourite/delete/masterclass/error': Error;

  // Delete favorite events - Learning Path
  'catalog/favourite/delete/learningPath/do': {
    favouriteId: string;
    userData?: UserData;
  };
  'catalog/favourite/delete/learningPath/success': void;
  'catalog/favourite/delete/learningPath/error': Error;
}
